import type { NotificationEventDefinition, NotificationPolicy, TicketPolicy } from '@common/types';

import { DEPARTMENT_ID } from './departments.seed';

export const TICKET_POLICY_SEED: TicketPolicy = {
  organizationId: 'sun-intl',
  slaTargets: [
    { urgency: 'normal', firstResponseHours: 24, resolutionHours: 72 },
    { urgency: 'escalated', firstResponseHours: 8, resolutionHours: 24 },
    { urgency: 'critical', firstResponseHours: 2, resolutionHours: 8 },
  ],
  escalation: {
    afterBreachHours: 4,
    notifyRoleId: null,
    notifyLabel: 'General Manager',
    raiseUrgency: true,
  },
  autoTicketRules: [
    {
      id: 'auto-1',
      questionId: 'q-001-question-1',
      questionnaireId: 'q-001',
      triggerType: 'rating_threshold',
      queueId: 'sun-intl-central',
      ownerId: null,
      workflow: 'central_complaint',
      questionLabel: 'How would you rate the cleanliness of your room?',
      departmentId: DEPARTMENT_ID.HOUSEKEEPING,
      threshold: 2,
      urgency: 'escalated',
      isEnabled: true,
    },
    {
      id: 'auto-2',
      questionId: 'q-001-question-2',
      questionnaireId: 'q-001',
      triggerType: 'rating_threshold',
      queueId: 'sun-intl-central',
      ownerId: null,
      workflow: 'central_complaint',
      questionLabel: 'How would you rate your check-in experience?',
      departmentId: DEPARTMENT_ID.FRONT_DESK,
      threshold: 2,
      urgency: 'normal',
      isEnabled: true,
    },
    {
      id: 'auto-3',
      questionId: 'q-001-question-3',
      questionnaireId: 'q-001',
      triggerType: 'rating_threshold',
      queueId: 'sun-intl-central',
      ownerId: null,
      workflow: 'central_complaint',
      questionLabel: 'How would you rate the food and beverage offering?',
      departmentId: DEPARTMENT_ID.FOOD_BEVERAGE,
      threshold: 2,
      urgency: 'normal',
      isEnabled: false,
    },
    {
      id: 'auto-4',
      questionId: 'q-001-question-4',
      questionnaireId: 'q-001',
      triggerType: 'rating_threshold',
      queueId: 'sun-intl-central',
      ownerId: null,
      workflow: 'central_complaint',
      questionLabel: 'Was anything in your room not working?',
      departmentId: DEPARTMENT_ID.MAINTENANCE,
      threshold: 3,
      urgency: 'critical',
      isEnabled: true,
    },
  ],
  requiredResolutionFields: ['feedbackType', 'dateOfResolution', 'closingNotes'],
};

export const NOTIFICATION_EVENT_CATALOG: ReadonlyArray<NotificationEventDefinition> = [
  {
    key: 'ticket.guest-reply',
    label: 'Guest replies to a ticket',
    description: 'A new message needs attention on your case.',
    icon: 'mark_chat_unread',
  },
  {
    key: 'review.low-score',
    label: 'Low-scoring review arrives',
    description: 'A review lands at or below the alert threshold.',
    icon: 'sentiment_dissatisfied',
  },
  {
    key: 'review.new',
    label: 'Any new review',
    description: 'Every review from every connected source.',
    icon: 'reviews',
  },
  {
    key: 'ticket.assigned',
    label: 'Ticket assigned to me',
    description: 'You are named as the assignee on a service ticket.',
    icon: 'assignment_ind',
  },
  {
    key: 'ticket.sla-breached',
    label: 'Ticket breaches its SLA',
    description: 'First response or resolution passes the target set in ticket policy.',
    icon: 'timer_off',
  },
  {
    key: 'survey.response-spike',
    label: 'Response volume spikes',
    description: 'Survey responses jump well above the trailing average.',
    icon: 'trending_up',
  },
  {
    key: 'integration.failed',
    label: 'An integration fails',
    description: 'A review source or data import stops working.',
    icon: 'sync_problem',
  },
  {
    key: 'digest.weekly',
    label: 'Weekly summary',
    description: 'A rolled-up digest of the week across the active scope.',
    icon: 'mail',
  },
];

export const NOTIFICATION_POLICY_SEED: NotificationPolicy = {
  scopeId: 'sun-intl',
  digestCadence: 'weekly',
  quietHoursFrom: '21:00',
  quietHoursTo: '07:00',
  rules: [
    {
      event: 'ticket.guest-reply',
      channels: ['in-app', 'email'],
      recipientKind: 'named',
      recipientLabel: 'Case owner',
      isEnabled: true,
    },
    {
      event: 'review.low-score',
      channels: ['in-app', 'email'],
      recipientKind: 'role',
      recipientLabel: 'Guest Relations',
      isEnabled: true,
    },
    {
      event: 'review.new',
      channels: ['digest'],
      recipientKind: 'role',
      recipientLabel: 'Property Manager',
      isEnabled: true,
    },
    {
      event: 'ticket.assigned',
      channels: ['in-app', 'email'],
      recipientKind: 'named',
      recipientLabel: 'The assignee',
      isEnabled: true,
      isMandatory: true,
    },
    {
      event: 'ticket.sla-breached',
      channels: ['in-app', 'email'],
      recipientKind: 'role',
      recipientLabel: 'General Manager',
      isEnabled: true,
    },
    {
      event: 'survey.response-spike',
      channels: ['digest'],
      recipientKind: 'role',
      recipientLabel: 'Group Insights',
      isEnabled: false,
    },
    {
      event: 'integration.failed',
      channels: ['email'],
      recipientKind: 'role',
      recipientLabel: 'Group IT',
      isEnabled: true,
    },
    {
      event: 'digest.weekly',
      channels: ['email'],
      recipientKind: 'role',
      recipientLabel: 'All managers',
      isEnabled: true,
    },
  ],
};
